// /admin — password-gated launcher for the operator's tools, and the Dashboard:
// Analytics for stakeholders, the route and Nearby logs, the phone app's
// numbers, the shuttle and the changelog.
//
// The split, since the Route tester landed (2026-10):
//
//   Dashboard                  what the campus is DOING — analytics and logs,
//                              nothing to act on.
//   Volunteer Admin Dashboard  everything the operator does ABOUT the
//                              volunteers' work (components/VolunteerAdmin.jsx):
//                              who has access, the issues In Progress, the
//                              ratings log, the restaurant queue, room-name
//                              publishing, the Content Filler's edits and the
//                              Route tester's suggestions. The "In progress",
//                              "Route ratings" and "Restaurants" tabs that used
//                              to be on the Dashboard are tabs there now.
//   Accounts, Security         in-page, as before.
//   /volunteer/*, the rest     links; each keeps its own sign-in.
//
// ⚠️ The Admin Revamp (docs/ADMIN_REVAMP.md) DELETED the Sign scans, Photos,
// Emails, Survey answers and Usage pages. Their numbers that matter moved onto
// Analytics; the rest was downloaded and let go. Reports, route triage and the
// Events manager moved to Volunteer Tools (/volunteer/issues,
// /volunteer/moderate) — the "In progress" list, where a volunteer's findings
// come back to the operator to be marked Fixed, is the Volunteer Admin
// Dashboard's second tab.
//
// Login posts ADMIN_USER + ADMIN_PASSWORD to /api/auth with scope "admin" — the
// central credential that opens every tool. (Until ADMIN_PASSWORD is set the
// same form still accepts APP_PASSWORD with any username, so this page cannot
// lock its operator out before the variable exists.) /api/auth
// returns a short-lived signed token; that token is then sent as a Bearer to
// /api/admin-data (which requires it). The token lives in sessionStorage so a
// refresh keeps you logged in until it expires (~6h) or the tab closes.

import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import AccountsManager from './components/AccountsManager.js';
import AnalyticsTab from './components/AnalyticsTab.jsx';
import VolunteerAdmin from './components/VolunteerAdmin.jsx';
import SecurityPanel from './components/SecurityPanel.jsx';
import ChangelogAdmin from './components/ChangelogAdmin.js';
import FinalExamsAdmin from './components/FinalExamsAdmin.js';
import SafetyAdmin from './components/SafetyAdmin.js';
import PopupsAdmin, { unseenAttempts } from './components/PopupsAdmin.js';
import ShuttleAdmin from './components/ShuttleAdmin.jsx';
import UMapsWordmark from './components/UMapsWordmark.jsx';
import { ChartCard, TrendChart, HourChart, DonutChart } from './components/Charts.jsx';
// The vocabularies and the exports live outside this file so the SPREADSHEETS
// and the tables cannot drift apart — a column reading `faculty:u1` where the
// screen said "University 1" is the kind of difference nobody notices until
// somebody is holding the file in a meeting.
import {
  fmtTime, optionTags, FAIL_LABELS, KIND_LABELS, NEARBY_KINDS,
  YEAR_LABELS, FACULTY_LABELS, COMMUTE_LABELS,
  triageStatus, isOutdoorStart,
} from './utils/adminLabels.js';
import { weekOf, weekLabel, weekRange, rangeLabel, downloadsSince, APP_RELEASE_DAY } from './utils/analytics.js';
import {
  canExport, exportRowCount, pageSheetCount, pageNeedsAll,
  downloadPageXlsx, downloadPageCsv, downloadAllXlsx,
} from './utils/adminExport.js';

// In dev the API runs on the Express server (port 3001); in prod it's same-origin.
const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';
const TOKEN_KEY = 'umaps-admin-token';


/**
 * The App-questions charts, derived from the two sources that can honestly
 * answer them.
 *
 * ⚠️ THE TWO SOURCES ARE NOT INTERCHANGEABLE, and picking the wrong one gives a
 * chart that is quietly wrong rather than obviously broken:
 *
 *  · `appProfileDaily` — per-day counter hashes with NO TTL. The only thing
 *    that can draw a line back over terms. `installs` is distinct phones that
 *    day; `answers` counts submissions, so somebody correcting their faculty in
 *    Settings moves it again. "Signups" means installs.
 *  · `appProfiles` — the raw rows, a rolling 5,000-entry sample with a one-year
 *    TTL. The ONLY source carrying a timestamp precise enough for an hour, and
 *    therefore the only way to draw the by-hour chart — but a line drawn from
 *    it would flatten to zero at the far end as rows aged out, which is why the
 *    trend does not use it.
 *  · `appProfileCounts` — all-time rollups, counted once per install. What the
 *    shares are drawn from, for the same reason the tables use it.
 */
function chartData(data) {
  const daily = data?.appProfileDaily || [];
  const rows = data?.appProfiles || [];
  const c = data?.appProfileCounts || {};
  const n = (f) => Number(c[f]) || 0;

  // ⚠️ Reversed: every store here returns days newest-first, and a line drawn
  // in that order runs backwards through time without looking wrong.
  const asc = [...daily].reverse();

  const hours = new Array(24).fill(0);
  for (const r of rows) {
    if (!r?.ts) continue;
    const d = new Date(r.ts);
    if (!Number.isNaN(d.getTime())) hours[d.getHours()] += 1;
  }

  // A pie of twenty-one faculties is a colour wheel, not an answer. The tail is
  // kept as one honest slice rather than dropped, so the shares still sum to
  // everybody and the full list is in the table below.
  const facultyAll = Object.entries(FACULTY_LABELS)
    .map(([key, label]) => ({ label, value: n(`faculty:${key}`) }))
    .filter((s) => s.value > 0)
    .sort((a, b) => b.value - a.value);
  const TOP = 6;
  const facultyTail = facultyAll.slice(TOP).reduce((t, s) => t + s.value, 0);
  const facultySlices = [
    ...facultyAll.slice(0, TOP),
    ...(facultyTail ? [{ label: `Other (${facultyAll.length - TOP} more)`, value: facultyTail }] : []),
    ...(n('faculty:skipped') ? [{ label: 'Skipped', value: n('faculty:skipped') }] : []),
  ];

  return {
    trendInstalls: asc.map((d) => ({ day: d.day, value: Number(d.installs) || 0 })),
    trendAnswers: asc.map((d) => ({ day: d.day, value: Number(d.answers) || 0 })),
    trendDays: asc.length,
    hours,
    hoursFrom: rows.length,
    yearSlices: [
      ...Object.entries(YEAR_LABELS).map(([key, label]) => ({ label, value: n(`year:${key}`) })),
      { label: 'Skipped', value: n('year:skipped') },
    ],
    facultySlices,
    platformSlices: [
      { label: 'iPhone', value: n('ios') },
      { label: 'Android', value: n('android') },
    ],
    accessibleSlices: [
      { label: 'On', value: n('accessible:yes') },
      { label: 'Off', value: n('accessible:no') },
      { label: 'Skipped', value: n('accessible:skipped') },
    ],
  };
}


// The tools /admin is the front door to. The admin session FOLLOWS THROUGH:
// Building Directories and the Event Creator pick up this page's token (see
// utils/adminSession.js), so the operator arrives already signed in rather than
// retyping the same password at the next door. The cards navigate in the same
// tab, which is what keeps sessionStorage with them.
//
// The tools that are not wired to it yet say so on their card, so nobody is
// surprised by a password box.
const TOOLS = [
  {
    key: 'dashboard', icon: '📊', name: 'Dashboard',
    desc: 'Analytics for stakeholders, route and Nearby logs, the phone app, the shuttle and the changelog.',
    gate: 'This login',
  },
  {
    // The operator's side of every Volunteer Tool, in one place: access,
    // issues in progress, the ratings log, the restaurant queue, room-name
    // publishing, content edits and route suggestions. In-page, because
    // most of what it does is operator-only on the server.
    key: 'volunteeradmin', icon: '🗂️', name: 'Volunteer Admin Dashboard',
    desc: 'Issues in progress, route suggestions from the tester, room names to publish, content edits, restaurants and the ratings log.',
    gate: 'This login',
  },
  {
    key: 'accounts', icon: '👤', name: 'Accounts',
    desc: 'The logins we issue — Building Directories, the Eng Tour Creator and the Scavenger Hunt Admin.',
    gate: 'This login',
  },
  {
    key: 'security', icon: '🔒', name: 'Security',
    desc: 'Devices approved to sign in as admin, and every failed or blocked sign-in attempt.',
    gate: 'This login',
  },
  {
    // Who may open Volunteer Tools, and which of the five. The Volunteer
    // Admin Dashboard's first tab, opened straight to it: granting access is
    // the operator's act and nobody else's.
    key: 'volunteers', icon: '🙋', name: 'Volunteer access',
    tool: 'volunteeradmin', tab: 'access',
    desc: 'Grant or revoke Volunteer Tools, and choose which of the five each person opens.',
    gate: 'This login',
  },
  {
    // ⚠️ These two USED to be in here: Events Admin was an in-page tool and
    // Reports a dashboard tab. The Admin Revamp moved both to Volunteer Tools,
    // so a volunteer can do the work without the operator's password. They
    // stay on this launcher as links so the operator can still get there in
    // one tap.
    key: 'moderate', icon: '🎪', name: 'Event & review moderation',
    href: '/volunteer/moderate',
    desc: 'Approve or deny new events, change a listed event\'s status, moderate restaurant reviews and dish photos.',
    gate: 'Volunteer Tools',
  },
  {
    key: 'issues', icon: '🧰', name: 'Reports & bad routes',
    href: '/volunteer/issues',
    desc: 'Every problem report and bad route rating, with notes. Ones marked In Progress come back here.',
    gate: 'Volunteer Tools',
  },
  {
    // Drafts only: what volunteers type here reaches students once the
    // operator publishes them (Volunteer Admin Dashboard → Room names).
    key: 'rooms', icon: '🚪', name: 'Room names',
    href: '/volunteer/rooms',
    desc: 'Friendly names for room numbers, campus-wide. Publish a batch from the Volunteer Admin Dashboard.',
    gate: 'Volunteer Tools',
  },
  {
    // Test edits to the nav graph — never the map data. What a volunteer
    // suggests from here comes back as a Route suggestion on the Volunteer
    // Admin Dashboard.
    key: 'routes', icon: '🧪', name: 'Route tester',
    href: '/volunteer/routes',
    desc: 'Route between any two points, compare variants, try blocking a node or a shaft, and suggest a fix.',
    gate: 'Volunteer Tools',
  },
  {
    key: 'buildingdirectories', icon: '🔳', name: 'Building Directories',
    href: '/buildingdirectories',
    desc: 'Place and print the QR signage for a building.',
    gate: 'Signed in with this login',
  },
  {
    key: 'engtourcreator', icon: '🧭', name: 'Eng Tour Creator',
    href: '/engtourcreator',
    desc: 'Build and edit the engineering tour.',
    gate: 'Asks for a Tour Creator account',
  },
  {
    key: 'phototracker', icon: '📷', name: 'Photo Tracker',
    href: '/phototracker',
    desc: 'Field tool for photographing nodes and places.',
    gate: 'Asks for the admin password',
  },
  {
    key: 'parkconnectadmin', icon: '🅿️', name: 'ParkConnect Queue',
    href: '/parkconnectadmin',
    desc: 'Moderate the permit-sharing board.',
    gate: 'Asks for the admin password',
  },
];

export default function AdminPage() {
  const [token, setToken] = useState(() => {
    try { return sessionStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
  });
  const [user, setUser] = useState('');
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  // Analytics first: it is the page this dashboard is opened to SHOW, and the
  // one thing on it that makes sense to somebody who is not the operator.
  const [tab, setTab] = useState('analytics');
  const [openRoute, setOpenRoute] = useState(null);   // expanded route group, by pair key
  // Outdoor auto-start rows whose node id is revealed, by the same row key.
  // A Set rather than one id: comparing two outdoor starts side by side is the
  // reason to open one.
  const [openFromNode, setOpenFromNode] = useState(() => new Set());
  const toggleFromNode = useCallback((key) => setOpenFromNode((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  }), []);
  const [tool, setTool] = useState('home');           // 'home' launcher | 'dashboard' | …
  // Which tab the Volunteer Admin Dashboard opens on: the "Volunteer access"
  // card lands on its Access tab, the dashboard card on the first tab.
  const [volTab, setVolTab] = useState('access');
  // The Routes page shows ONE Sunday–Saturday week at a time. null = the newest.
  const [routeWeek, setRouteWeek] = useState(null);
  const [allRoutesBusy, setAllRoutesBusy] = useState(false);
  // Collapsed by default, with the total on the header: the tally is the
  // answer most visits want, and the per-code list is the detail behind it.
  const [referralsOpen, setReferralsOpen] = useState(false);
  // ⚠️ The changelog is fetched SEPARATELY from /api/admin-data, not folded
  // into it. admin-data is gated on `requireAdmin`, which still passes any
  // valid token while ADMIN_PASSWORD is unset; /api/changelog?all=1 requires
  // `tokenIsAdmin`. Drafts are unpublished writing about to go on the public
  // web, so they stay behind the stricter of the two gates.
  const [changelog, setChangelog] = useState([]);
  // Whether /api/popups has refused a write the operator has not looked at
  // yet — the red dot on the Popups tab. One small GET; the tab itself clears
  // it when opened (see PopupsAdmin's ATTEMPTS_SEEN_KEY).
  const [popupsAlert, setPopupsAlert] = useState(false);
  const [allBusy, setAllBusy] = useState(false);   // "Download everything" is fetching

  const saveToken = (t) => {
    setToken(t);
    try { t ? sessionStorage.setItem(TOKEN_KEY, t) : sessionStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
  };

  // A correct password from a browser that has never been approved gets a 403
  // with `verifyDevice` instead of a token (api/_adminGuard.js): an approval
  // link has gone to the security inbox. This page then waits — polling whether
  // THIS browser's device cookie has been approved — and signs in by itself
  // the moment it is, so approving on a phone finishes the sign-in on the
  // laptop without anything to retype. The password stays in memory only while
  // waiting, and is cleared on success or on giving up.
  const [waitDevice, setWaitDevice] = useState(null); // { message, since } | null

  const login = async (e) => {
    e?.preventDefault();
    setErr(''); setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/auth`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: user.trim(), password: pw, scope: 'admin' }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 403 && j.verifyDevice) {
        setWaitDevice((w) => ({ message: j.error, since: w?.since || Date.now(), mailFailed: !!j.mailFailed }));
        setBusy(false);
        return;
      }
      if (!r.ok || !j.token) { setErr(j.error || 'Login failed.'); setWaitDevice(null); setBusy(false); return; }
      setWaitDevice(null);
      saveToken(j.token);
      setPw('');
    } catch { setErr('Network error.'); }
    setBusy(false);
  };
  const loginRef = useRef(login);
  loginRef.current = login;

  useEffect(() => {
    if (!waitDevice || waitDevice.mailFailed) return undefined;
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      // Approval links last 15 minutes; past that, stop and say so.
      if (Date.now() - waitDevice.since > 16 * 60 * 1000) {
        setWaitDevice(null); setPw('');
        setErr('The approval link expired. Sign in again to get a new one.');
        return;
      }
      try {
        const r = await fetch(`${API_BASE}/api/admin-device`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ op: 'status' }),
        });
        const j = await r.json().catch(() => ({}));
        if (!stopped && j.trusted) { loginRef.current(); return; }
      } catch { /* keep waiting */ }
      if (!stopped) timer = setTimeout(tick, 3000);
    };
    let timer = setTimeout(tick, 3000);
    return () => { stopped = true; clearTimeout(timer); };
  }, [waitDevice]);

  const load = useCallback(async () => {
    if (!token) return;
    setBusy(true); setErr('');
    try {
      // No `limit`: this screen is the operator's whole record, and a number
      // here silently truncates it — the App-signups tab is a work queue that
      // must list everybody still waiting, and a route count read off a
      // truncated table is simply wrong. The endpoint's default is everything
      // each store holds.
      const r = await fetch(`${API_BASE}/api/admin-data`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.status === 401) { saveToken(''); setData(null); setErr('Session expired — log in again.'); setBusy(false); return; }
      const j = await r.json();
      if (!r.ok) { setErr(j.error || 'Failed to load.'); setBusy(false); return; }
      setData(j);
    } catch { setErr('Failed to load.'); }
    setBusy(false);
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  // Drafts included — this is the authoring side. A failure here is left silent
  // rather than filling `err`: the changelog is one tab of eleven, and it must
  // not put an error banner over a dashboard somebody opened to read the route
  // table. The tab shows an empty list, which is what it would show anyway.
  const loadChangelog = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API_BASE}/api/changelog?all=1`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json().catch(() => ({}));
      if (r.ok && j.ok) setChangelog(Array.isArray(j.posts) ? j.posts : []);
    } catch { /* leave the list as it was */ }
  }, [token]);

  useEffect(() => { if (token) loadChangelog(); }, [token, loadChangelog]);

  // Same shape as the changelog read, same silence on failure: a tab's badge
  // must not put an error banner over the dashboard.
  const loadPopupsBadge = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API_BASE}/api/popups?admin=1`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json().catch(() => ({}));
      if (r.ok && j.ok) setPopupsAlert(unseenAttempts(j.attempts) > 0);
    } catch { /* leave the badge as it was */ }
  }, [token]);

  useEffect(() => { if (token) loadPopupsBadge(); }, [token, loadPopupsBadge]);

  /**
   * The WHOLE route log, walked a page at a time, for the downloads.
   *
   * ⚠️ The dashboard payload carries a recent slice (see api/admin-data.js):
   * the log retains 50,000 searches and a realistic row is ~525 bytes, so the
   * full history is ~26 MB and cannot come back in one response at all. Before
   * this, that ceiling WAS the export — the file stopped at the retention cap
   * and looked complete, which is the worst shape for a record somebody builds
   * a term-over-term claim on.
   *
   * Bounded by `total` rather than by trusting the pages to run out, and by a
   * page ceiling besides: this is a loop making network calls, and an endpoint
   * that answered oddly should end the download, not the browser tab.
   */
  const fetchAllRoutes = useCallback(async (current) => {
    const PAGE = 5000;
    const MAX_PAGES = 40;                     // 200,000 rows — far past retention
    const out = [];
    let offset = 0;
    for (let i = 0; i < MAX_PAGES; i++) {
      const r = await fetch(`${API_BASE}/api/admin-data?part=routes&offset=${offset}&limit=${PAGE}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) break;
      const j = await r.json().catch(() => ({}));
      const page = Array.isArray(j.routes) ? j.routes : [];
      out.push(...page);
      offset += page.length;
      // A short page is the end of the list; a total we have reached is the
      // same thing said by the server. Either stops us.
      if (page.length < PAGE || (j.total && offset >= j.total)) break;
    }
    // Never hand back LESS than the screen already has — a failed first page
    // would otherwise turn a partial download into an empty one.
    return out.length >= (current?.routes || []).length ? { ...current, routes: out } : current;
  }, [token]);

  /**
   * Beta signups from /mobile, split by platform.
   *
   * `waiting` is the only number that is a to-do: an Android signup does
   * nothing for the student until their Play Store address is in the Play
   * Console, so an uninvited row is a person currently unable to install the
   * app and waiting on us. iOS has no equivalent — the TestFlight link is
   * public and works the moment they tap it.
   */
  /**
   * Referral counts, defaulted so an older /api/admin-data (or a failed read,
   * which that endpoint turns into an empty shape) renders zeros rather than
   * throwing on `.rows` — this tab has four other jobs that must survive it.
   */
  const referrals = data?.referrals || { rows: [], codes: 0, referred: 0 };

  /**
   * Downloads of the RELEASED app — every distinct phone that sent its first-run
   * answers on or after the 2026-10-05 store release, folded by
   * `downloadsSince`. Nothing before that day counts: those phones were the
   * beta, and every one of them had to download the app again from a store.
   * The beta's own numbers stay on App questions (the all-time shares) and
   * Beta signups (who asked to test).
   */
  const downloads = useMemo(() => downloadsSince(data?.appProfileDaily), [data]);

  const mobile = useMemo(() => {
    const rows = data?.mobileSignups || [];
    const ios = rows.filter(r => r.platform === 'ios');
    const android = rows.filter(r => r.platform === 'android');
    // The rows themselves, not just the count: three of the buttons below act on
    // this exact set — paste these into the console, write to these people, tick
    // these off — and they must be the same set in all three or the operator
    // ends up emailing somebody they never added.
    const uninvited = android.filter(r => !r.invited);
    // Every student who has signed up for the app on either platform, once
    // each. ⚠️ DEDUPED, unlike the per-platform lists: a signup is keyed on
    // platform + school email, so somebody who asked for the beta on their
    // phone and their iPad is two rows carrying ONE address — and pasting that
    // into a To: field sends them the same announcement twice. Addresses are
    // stored trimmed and lower-cased (api/_mobileSignups.js), so an exact-match
    // Set is the whole of the comparison.
    const students = [...new Set(rows.map(r => r.email).filter(Boolean))];
    return {
      rows, ios, android, uninvited, students,
      total: rows.length,
      waiting: uninvited.length,
    };
  }, [data]);

  /**
   * Mark an Android signup as added to the Play Console (or undo it).
   *
   * Optimistic: the row flips immediately and the list is reloaded after. This
   * is a checklist worked through twenty rows at a time, and a full reload
   * between every tick makes it feel broken.
   */
  const [mobileBusy, setMobileBusy] = useState('');
  const setInvited = useCallback(async (row, invited) => {
    setMobileBusy(row.email); setErr('');
    try {
      const r = await fetch(`${API_BASE}/api/mobile-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ op: 'invited', platform: row.platform, email: row.email, invited }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.ok === false) { setErr(j.error || 'That did not go through.'); return; }
      await load();
    } catch { setErr('Network error.'); }
    finally { setMobileBusy(''); }
  }, [token, load]);

  /**
   * Tick off every row that was waiting, in one request.
   *
   * The queue is worked in blocks — copy the addresses, paste them into the Play
   * Console, then say so here — and doing that last part one row at a time is
   * fifty taps for one decision that was already made when the paste went in.
   *
   * ⚠️ It sends the ADDRESSES it can see, not "mark everything uninvited". A
   * student who signs up while the operator is in the Play Console has not been
   * added to anything, and sweeping them into this tick would quietly drop them
   * out of the only queue that says they are waiting.
   */
  const markAllInvited = useCallback(async (rows) => {
    if (!rows.length) return;
    if (!window.confirm(
      `Mark ${rows.length} Android tester${rows.length === 1 ? '' : 's'} as added to the Play Console?\n\n`
      + 'Do this after their Play Store addresses are actually pasted in — anyone marked '
      + 'added drops off the "waiting on you" list. You can untick a row individually.')) return;
    setMobileBusy('all'); setErr('');
    try {
      const r = await fetch(`${API_BASE}/api/mobile-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          op: 'invited', platform: 'android', invited: true, emails: rows.map(x => x.email),
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.ok === false) { setErr(j.error || 'That did not go through.'); return; }
      // Said out loud, because the number can legitimately differ from what the
      // button offered — a row already ticked in another tab, one deleted since.
      if (j.missing) setErr(`Marked ${j.changed} as added. ${j.missing} were no longer in the list.`);
      await load();
    } catch { setErr('Network error.'); }
    finally { setMobileBusy(''); }
  }, [token, load]);

  // "Nearby" panel rollups: sums over whatever days were recorded, plus the
  // per-place tallies that answer "which spots do people actually look at".
  const nearby = useMemo(() => {
    const days = data?.nearby?.daily || [];
    const places = data?.nearby?.places || [];
    const sum = (f) => days.reduce((n, d) => n + (d[f] || 0), 0);
    const pct = (n, of) => (of ? Math.round((n / of) * 100) : null);
    const views = sum('ev:view');
    const kinds = NEARBY_KINDS.map(k => ({
      kind: k,
      views: sum(`kind:${k}`),
      empty: sum(`empty:${k}`),
    }));
    const kindTotal = kinds.reduce((n, k) => n + k.views, 0);
    // ⚠️ The "Filters people reach for" table was REMOVED (Admin Revamp): the
    // store still counts `filter:*`, nothing reads it any more.
    //
    // Most-looked-at places, GROUPED BY KIND, most-viewed first inside each.
    // One flat ranking put 126 washrooms between the study spots and made
    // "which library" a scroll; the question is always asked about one kind.
    // Kinds run in the panel's own tab order, and a kind the app invents later
    // still gets a group of its own, at the end, under its raw id.
    const order = [...NEARBY_KINDS];
    for (const p of places) if (p.kind && !order.includes(p.kind)) order.push(p.kind);
    const placeGroups = [...order, null].map((k) => {
      const list = places
        .filter((p) => (k === null ? !p.kind : p.kind === k))
        .sort((a, b) => (b.views || 0) - (a.views || 0) || (b.gos || 0) - (a.gos || 0));
      return {
        kind: k,
        label: k === null ? 'Unknown kind' : (KIND_LABELS[k] || k),
        places: list,
        views: list.reduce((n, p) => n + (p.views || 0), 0),
        gos: list.reduce((n, p) => n + (p.gos || 0), 0),
      };
    }).filter((g) => g.places.length);
    return {
      days, places, placeGroups, views, kinds, kindTotal,
      opens: sum('ev:open'),
      gos: sum('ev:go'),
      sessions: sum('sessions'),
      empties: sum('ev:empty'),
      goPct: pct(sum('ev:go'), views),
      sharePct: (k) => pct(k, kindTotal),
    };
  }, [data]);

  /**
   * What people did with the club events — the other half of Events Admin.
   *
   * Events Admin says what was published and whether its club is verified. This
   * says how the event LAYER is doing — the half that is about the feature
   * rather than about any one event.
   *
   * ⚠️ The per-event figures are deliberately NOT here. They are on each event's
   * card in /eventmapcreator, where the club that made the event can read its
   * own, and `admin-data` no longer even fetches them: a payload carrying a
   * table nothing renders is how the two drift. `readEventDaily` is the only
   * half this screen asks for.
   *
   * ⚠️ `opens` is a counter of its own, not `ev:pin` plus `ev:row`. The two ways
   * of opening an event are kept apart so "is the map overlay worth drawing" is
   * answerable, and summing them here as well as in the store is how the
   * headline and the day table start disagreeing — so the sum is read, never
   * re-derived. Same rule the route log's `countsFor` follows.
   */
  const eventUse = useMemo(() => {
    const days = data?.eventUse?.daily || [];
    const sum = (f) => days.reduce((n, d) => n + (d[f] || 0), 0);
    const pct = (n, of) => (of ? Math.round((n / of) * 100) : null);
    const opens = sum('opens');
    return {
      days, opens,
      pins: sum('ev:pin'),
      rows: sum('ev:row'),
      directions: sum('ev:directions'),
      saves: sum('ev:save'),
      unsaves: sum('ev:unsave'),
      tickets: sum('ev:tickets'),
      links: sum('ev:link'),
      clubs: sum('ev:club'),
      sessions: sum('sessions'),
      // Of everybody who opened an event, how many asked to be walked there.
      // The one number that separates "a poster people read" from "a place
      // people went", which is the whole reason a club puts an event on a map.
      goPct: pct(sum('ev:directions'), opens),
      // Which way in. A map pin is the expensive half of this feature — it is
      // drawn on every campus screen — so its share is what says whether it
      // earns that.
      pinPct: pct(sum('ev:pin'), opens),
    };
  }, [data]);

  /**
   * The mobile questionnaire, folded into the four things it can honestly say.
   *
   * Read from the ALL-TIME rollups, not from the rows: the rows are a capped
   * sample and are counted once per install by the store, which is the
   * difference between "how many students are in Engineering" and "how many
   * times was a form saved" — somebody who corrects their faculty in Settings
   * would otherwise appear in two of them at once.
   *
   * Every option is listed even at zero, including "Skipped". A distribution
   * that hides its empty rows reads as though nobody could have chosen them,
   * and on this screen the skips are half the point: a question most people
   * decline is one to cut rather than one to keep asking.
   */
  const appProfile = useMemo(() => {
    const c = data?.appProfileCounts || {};
    const n = (f) => c[f] || 0;
    const answered = n('answers');
    const pct = (v) => (answered ? Math.round((v / answered) * 100) : null);
    const dist = (prefix, labels) => [
      ...Object.entries(labels).map(([key, label]) => ({ key, label, n: n(`${prefix}:${key}`) })),
      { key: 'skipped', label: 'Skipped', n: n(`${prefix}:skipped`), skipped: true },
    ];
    return {
      answered,
      pct,
      rows: data?.appProfiles || [],
      ios: n('ios'),
      android: n('android'),
      years: dist('year', YEAR_LABELS),
      // Biggest first: the reason to look at this table is to find out who is
      // actually here, and alphabetical order buries that under Agriculture.
      faculties: dist('faculty', FACULTY_LABELS).sort((a, b) => (a.skipped ? 1 : b.skipped ? -1 : b.n - a.n)),
      // Every overlay starts ON, so what this measures is who turned one OFF —
      // and the honest denominator for that is everyone who answered.
      commutes: Object.entries(COMMUTE_LABELS).map(([key, label]) => ({
        key, label, on: n(`commute:${key}`), off: n(`commute-off:${key}`),
      })),
      accessible: n('accessible:yes'),
      accessibleNo: n('accessible:no'),
      accessibleSkipped: n('accessible:skipped'),
      firstYear: n('year:1'),
      withCourses: n('courses:some'),
      ...chartData(data),
    };
  }, [data]);

  /**
   * Consecutive searches for the SAME From → To collapse into one row.
   *
   * One person deciding whether to take the tunnel emits a fresh search on every
   * toggle, so an untouched log reads as four or five journeys when there was
   * one. That inflates the headline number and, worse, buries the distinct
   * journeys underneath it. The row therefore shows the pair once and keeps the
   * attempts behind a dropdown, where the sequence of toggles is the useful
   * part: it is a record of somebody trying to find a route they can actually
   * use.
   *
   * Merging normally requires the SAME session. Two people searching the same
   * pair back to back is a coincidence, not one journey, and over-merging
   * silently deletes somebody's search while under-merging only costs a
   * duplicated row.
   *
   * ⚠️ With NO session id on either row, adjacent identical pairs merge inside a
   * SESSION_GAP_MS window. Storage can be blocked (a private window, a locked-
   * down browser) and every row from that visit then arrives session-less, which
   * used to mean nothing merged at all: one person poking at the same bus stop
   * for seven minutes produced eight identical rows that the table presented as
   * eight journeys. Two DIFFERENT people searching the same From→To pair, in the
   * same minutes, both with storage blocked, is rare enough to be worth the
   * trade — and nothing is deleted either way: the attempts stay in the
   * dropdown.
   */
  const routeGroups = useMemo(() => {
    const rows = data?.routes || [];
    const SESSION_GAP_MS = 15 * 60 * 1000;
    const pairKey = (r) => [
      r.from?.text || '', r.to?.building || '', r.to?.room || '', r.to?.nodeId || '',
    ].join('|');
    const out = [];
    for (const r of rows) {
      const prev = out[out.length - 1];
      const samePair = prev && prev.pair === pairKey(r);
      const sameSession = prev && r.session && prev.session === r.session;
      // Rows arrive newest-first, so "prev" is the LATER search of the two.
      const closeInTime = prev && !r.session && !prev.session
        && Math.abs((prev.tries[prev.tries.length - 1].ts || 0) - (r.ts || 0)) <= SESSION_GAP_MS;
      if (samePair && (sameSession || closeInTime)) { prev.tries.push(r); continue; }
      out.push({ pair: pairKey(r), session: r.session || null, tries: [r] });
    }
    return out.map(g => ({
      ...g,
      // Rows arrive newest-first, so the first attempt in a group is the LAST
      // one the person made — which is the one that reflects what they settled
      // on. `found` is true if any attempt worked: the journey succeeded even if
      // the accessible variant of it did not.
      latest: g.tries[0],
      found: g.tries.some(t => t.found),
      meters: (g.tries.find(t => t.found) || {}).meters ?? null,
    }));
  }, [data]);

  /**
   * The Routes page's week strip: every Sunday–Saturday week from the first
   * one anything was recorded in to this one, NEWEST FIRST, with no gaps.
   *
   * ⚠️ TWO COUNTS, because the rows and the rollups do not cover the same
   * span. `journeys` is counted from the rows this screen holds, which are a
   * recent slice of the log (see api/admin-data.js). `searches` is counted from
   * the per-day rollups, which never expire — so a week older than the slice
   * still appears in the strip with its true number, and the page says its rows
   * are not loaded rather than presenting the week as empty.
   */
  const routeWeeks = useMemo(() => {
    const journeys = new Map();
    for (const g of routeGroups) {
      const w = weekOf(g.latest?.ts || 0);
      if (w) journeys.set(w, (journeys.get(w) || 0) + 1);
    }
    const searches = new Map();
    for (const d of data?.daily || []) {
      const w = weekOf(d.day);
      if (w) searches.set(w, (searches.get(w) || 0) + (Number(d.searches) || 0));
    }
    return weekRange([...journeys.keys(), ...searches.keys()]).map((key) => ({
      key, journeys: journeys.get(key) || 0, searches: searches.get(key) || 0,
    }));
  }, [routeGroups, data]);

  /**
   * How many issues a volunteer has marked In Progress — for the badge on the
   * Volunteer Admin Dashboard's launcher card and its In progress tab, only.
   * The list itself is read from /api/issues by `InProgressIssues`,
   * which owns the workflow's states; this count is the same rule
   * (`triageState` in api/_routeTriage.js: status `checking`, not archived)
   * applied to the rows /api/admin-data already carries, so drawing a badge
   * costs no second request.
   */
  const inProgressCount = useMemo(() => {
    const live = (r) => triageStatus(r) === 'checking' && !r.triage?.archived;
    return (data?.reports || []).filter(live).length
      + (data?.routeRatings || []).filter((r) => r.verdict === 'bad' && live(r)).length;
  }, [data]);

  // ── Waiting for a new device to be approved ────────────────────────────────
  if (!token && waitDevice) {
    return (
      <div style={S.page}>
        <div style={S.loginCard}>
          <UMapsWordmark tool="Admin" size={26} href={null} style={S.h1} />
          <div style={{ fontSize: 16, fontWeight: 800, margin: '6px 0 8px' }}>Approve this device</div>
          <div style={{ ...S.muted, margin: '0 0 14px', lineHeight: 1.5 }}>{waitDevice.message}</div>
          {!waitDevice.mailFailed && (
            <div style={{ ...S.muted, margin: '0 0 14px' }}>Waiting for approval… this page continues by itself.</div>
          )}
          <button style={S.btnPrimary} onClick={() => login()} disabled={busy}>
            {busy ? 'Checking…' : waitDevice.mailFailed ? 'Try again' : 'I approved it — continue'}
          </button>
          <button style={{ ...S.btnGhost, marginTop: 10 }} onClick={() => { setWaitDevice(null); setPw(''); }}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ── Login screen ───────────────────────────────────────────────────────────
  if (!token) {
    return (
      <div style={S.page}>
        <form style={S.loginCard} onSubmit={login}>
          <UMapsWordmark tool="Admin" size={26} href={null} style={S.h1} />
          <div style={S.muted}>Sign in with the UMaps admin account</div>
          <input
            style={S.input} autoFocus placeholder="Username"
            value={user} onChange={e => setUser(e.target.value)}
            autoComplete="username"
          />
          <input
            style={S.input} type="password" placeholder="Password"
            value={pw} onChange={e => setPw(e.target.value)}
            autoComplete="current-password"
          />
          {err && <div style={S.err}>{err}</div>}
          <button style={S.btnPrimary} type="submit" disabled={busy || !pw}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    );
  }

  const routes = data?.routes || [];
  const reports = data?.reports || [];
  const ratings = data?.routeRatings || [];

  // The Routes page's week: the one picked in the strip, else the newest.
  const selWeek = routeWeek && routeWeeks.some((w) => w.key === routeWeek) ? routeWeek : routeWeeks[0]?.key;
  const selWeekInfo = routeWeeks.find((w) => w.key === selWeek) || { journeys: 0, searches: 0 };
  const weekGroups = routeGroups.filter((g) => weekOf(g.latest?.ts || 0) === selWeek);
  // The oldest week whose rows this screen actually holds. Older weeks are in
  // the strip (from the rollups) but their rows are past the loaded slice.
  const oldestLoadedTs = routes.length ? Math.min(...routes.map((r) => r.ts || Infinity)) : null;
  const rowsMissing = (data?.routesTotal || 0) > routes.length
    && oldestLoadedTs != null && selWeek && selWeek <= weekOf(oldestLoadedTs);

  const loadAllRoutes = async () => {
    setAllRoutesBusy(true);
    try {
      const full = await fetchAllRoutes(data);
      if (full) setData(full);
    } finally { setAllRoutesBusy(false); }
  };

  // ── Tool launcher ──────────────────────────────────────────────────────────
  // /admin is the way in to everything now, not just the analytics. The tools
  // each keep their OWN login — they are gated on different accounts (a faculty,
  // a club, a tour editor) and this token does not carry those rights — so these
  // are links, not a single sign-on. Saying so on the card is the honest version
  // of a launcher that cannot actually launch you in.
  if (tool === 'home') {
    return (
      <div style={S.page}>
        <div style={S.wrap}>
          <div style={S.header}>
            <UMapsWordmark tool="Admin" size={26} href={null} style={S.h1} />
            <div style={S.headerActions}>
              <button style={S.btnGhost} onClick={() => { saveToken(''); setData(null); }}>Log out</button>
            </div>
          </div>
          {err && <div style={S.err}>{err}</div>}
          <div style={S.muted}>Pick a tool.</div>
          <div style={S.toolGrid}>
            {TOOLS.map(t => (
              <a key={t.key} style={S.toolCard}
                 href={t.href || undefined}
                 onClick={t.href ? undefined : (e) => {
                   e.preventDefault();
                   setVolTab(t.tab || 'access');
                   setTool(t.tool || t.key);
                 }}
                 {...(t.href ? { target: '_self', rel: 'noreferrer' } : {})}>
                <div style={S.toolIcon}>{t.icon}</div>
                <div style={S.toolName}>
                  {t.name}
                  {/* Work waiting on the operator, counted off the rows
                      /api/admin-data already carries — no extra request. */}
                  {t.key === 'volunteeradmin' && inProgressCount > 0 && (
                    <span style={S.toolBadge}>{inProgressCount} in progress</span>
                  )}
                </div>
                <div style={S.toolDesc}>{t.desc}</div>
                <div style={S.toolGate}>{t.gate}</div>
              </a>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Accounts ─────────────────────────────────────────────────────────────────
  if (tool === 'accounts') {
    return (
      <div style={S.page}>
        <div style={S.wrap}>
          <div style={S.header}>
            <div style={S.h1}>
              <button style={S.backBtn} onClick={() => setTool('home')} title="All tools">←</button>
              Accounts
            </div>
            <div style={S.headerActions}>
              <button style={S.btnGhost} onClick={() => { saveToken(''); setData(null); }}>Log out</button>
            </div>
          </div>
          <AccountsManager
            apiBase={API_BASE}
            token={token}
            onUnauthorized={() => { saveToken(''); setData(null); setErr('Session expired — log in again.'); }}
          />
        </div>
      </div>
    );
  }

  // ── Security ─────────────────────────────────────────────────────────────────
  if (tool === 'security') {
    return (
      <div style={S.page}>
        <div style={S.wrap}>
          <div style={S.header}>
            <div style={S.h1}>
              <button style={S.backBtn} onClick={() => setTool('home')} title="All tools">←</button>
              Security
            </div>
            <div style={S.headerActions}>
              <button style={S.btnGhost} onClick={() => { saveToken(''); setData(null); }}>Log out</button>
            </div>
          </div>
          <SecurityPanel
            apiBase={API_BASE}
            token={token}
            onUnauthorized={() => { saveToken(''); setData(null); setErr('Session expired — log in again.'); }}
          />
        </div>
      </div>
    );
  }

  // ── Volunteer Admin Dashboard ────────────────────────────────────────────────
  // Volunteer access, In progress, Route ratings, Restaurants, Room names,
  // Content edits, Route suggestions. It reads the ratings off `data` (already
  // fetched for the Dashboard) and fetches its own three small lists.
  if (tool === 'volunteeradmin') {
    return (
      <div style={S.page}>
        <div style={S.wrap}>
          <div style={S.header}>
            <div style={S.h1}>
              <button style={S.backBtn} onClick={() => setTool('home')} title="All tools">←</button>
              Volunteer Admin Dashboard
            </div>
            <div style={S.headerActions}>
              <button style={S.btnGhost} onClick={() => { saveToken(''); setData(null); }}>Log out</button>
            </div>
          </div>
          {err && <div style={S.err}>{err}</div>}
          <VolunteerAdmin
            apiBase={API_BASE}
            token={token}
            initialTab={volTab}
            data={data}
            onRefresh={load}
            inProgressCount={inProgressCount}
            onUnauthorized={() => { saveToken(''); setData(null); setErr('Session expired — log in again.'); }}
          />
        </div>
      </div>
    );
  }

  // ── Dashboard ────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <div style={S.wrap}>
        <div style={S.header}>
          <div style={S.h1}>
            <button style={S.backBtn} onClick={() => setTool('home')} title="All tools">←</button>
            Dashboard
          </div>
          <div style={S.headerActions}>
            {/* The whole dashboard as one workbook, in the header rather than
                on a tab: it is the only action here that is not about the page
                being looked at, and putting it on a tab would make it read as
                that tab's download. */}
            <button
              style={S.btnGhost}
              disabled={!data || allBusy}
              onClick={async () => {
                setAllBusy(true);
                try { await downloadAllXlsx(data, fetchAllRoutes); } finally { setAllBusy(false); }
              }}
            >{allBusy ? 'Collecting…' : 'Download everything'}</button>
            <button style={S.btnGhost} onClick={load} disabled={busy}>{busy ? 'Loading…' : 'Refresh'}</button>
            <button style={S.btnGhost} onClick={() => { saveToken(''); setData(null); }}>Log out</button>
          </div>
        </div>

        {err && <div style={S.err}>{err}</div>}

        {/* ⚠️ THE TABS ARE GROUPED BY WHAT THEY ARE FOR, not by what they hold.
            Analytics leads on its own: it is the page for somebody who is not
            the operator. Then what the map is asked for, and what the phone app
            reports. Nothing here is waiting on the operator any more: the
            In progress, Route ratings and Restaurants tabs moved to the
            Volunteer Admin Dashboard. The Admin Revamp took out Usage, Sign
            scans, Photos, Emails and Survey answers, and moved Reports into
            Volunteer Tools — see docs/ADMIN_REVAMP.md. */}
        <div style={S.tabBar}>
          <div style={S.tabGroup}>
            <button style={{ ...S.tab, ...S.tabLead, ...(tab === 'analytics' ? S.tabOn : {}) }} onClick={() => setTab('analytics')}>
              📈 Analytics
            </button>
          </div>

          <div style={S.tabGroup}>
            <span style={S.tabGroupLabel}>Campus map</span>
            <button style={{ ...S.tab, ...(tab === 'routes' ? S.tabOn : {}) }} onClick={() => setTab('routes')}>
              {/* Journeys, not searches: every toggle used to count as a trip. */}
              Routes ({routeGroups.length}{routeGroups.length !== routes.length ? ` of ${routes.length}` : ''})
            </button>
            <button style={{ ...S.tab, ...(tab === 'nearby' ? S.tabOn : {}) }} onClick={() => setTab('nearby')}>
              Nearby ({nearby.views})
            </button>
            {/* Under Campus map although most rows come from the phone: what is
                measured is the map's event layer, which both clients draw. */}
            <button style={{ ...S.tab, ...(tab === 'eventUse' ? S.tabOn : {}) }} onClick={() => setTab('eventUse')}>
              Events ({eventUse.opens})
            </button>
            {/* No count: the Shuttle tab fetches ?part=shuttle itself and polls
                it while open, for the tracker's last-ping age. */}
            <button style={{ ...S.tab, ...(tab === 'shuttle' ? S.tabOn : {}) }} onClick={() => setTab('shuttle')}>
              Shuttle
            </button>
          </div>

          <div style={S.tabGroup}>
            <span style={S.tabGroupLabel}>Phone app</span>
            {/* First, because since the store release it is the number the
                operator opens this group for: how many phones have the real
                app. Counted from the release day only — see downloadsSince. */}
            <button style={{ ...S.tab, ...(tab === 'downloads' ? S.tabOn : {}) }} onClick={() => setTab('downloads')}>
              App downloads ({downloads.total})
            </button>
            <button style={{ ...S.tab, ...(tab === 'appProfile' ? S.tabOn : {}) }} onClick={() => setTab('appProfile')}>
              App questions ({appProfile.answered})
            </button>
            {/* "Beta", not "App": the form that fed this list left /mobile with
                the release, so these rows are the record of who tested. */}
            <button style={{ ...S.tab, ...(tab === 'mobile' ? S.tabOn : {}) }} onClick={() => setTab('mobile')}>
              Beta signups ({mobile.total})
              {/* An Android signup is a job until its address is in the Play Console. */}
              {mobile.waiting > 0 && tab !== 'mobile' && <span style={S.tabDot} />}
            </button>
            {/* The page it writes is /mobile/changelog — the app talking back
                to the students in the two tabs beside it. */}
            <button style={{ ...S.tab, ...(tab === 'changelog' ? S.tabOn : {}) }} onClick={() => setTab('changelog')}>
              Changelog ({changelog.length})
            </button>
            {/* The final exam feed the app reads. Loads itself: one small
                record, and it must not ride the dashboard payload. */}
            <button style={{ ...S.tab, ...(tab === 'finalExams' ? S.tabOn : {}) }} onClick={() => setTab('finalExams')}>
              Final exams
            </button>
            {/* The app's Safety pill: opens, and taps on each number. Totals
                only (api/safety-log.js). Loads itself. */}
            <button style={{ ...S.tab, ...(tab === 'safety' ? S.tabOn : {}) }} onClick={() => setTab('safety')}>
              Safety
            </button>
            {/* The announcements the app shows at launch and at each feature's
                door. Loads itself; the dot is a refused write nobody has
                looked at yet (docs/POPUPS.md → Security). */}
            <button style={{ ...S.tab, ...(tab === 'popups' ? S.tabOn : {}) }} onClick={() => setTab('popups')}>
              Popups
              {popupsAlert && tab !== 'popups' && <span style={S.tabDot} />}
            </button>
          </div>
        </div>

        {tab === 'analytics' && (
          <AnalyticsTab
            data={data}
            token={token}
            apiBase={API_BASE}
            fetchAllRoutes={fetchAllRoutes}
            onExpired={() => { saveToken(''); setData(null); setErr('Session expired — log in again.'); }}
          />
        )}

        {tab === 'routes' && (
          <>
          <ActionBar>
            {/* The table groups searches into journeys; the file does not. */}
            <ActionGroup label="What the file holds" note={`Every one of the ${(data?.routesTotal ?? routes.length).toLocaleString('en-CA')} raw searches, one row each, all weeks — not the journeys grouped on screen. The session column is what rebuilds the grouping in a pivot.`}>
              <span style={S.sectionHint}>Grouping is a reading; the rows are the record.</span>
            </ActionGroup>
            <ExportGroup tab="routes" data={data} hydrate={fetchAllRoutes} />
          </ActionBar>

          {/* ⚠️ ONE WEEK AT A TIME, Sunday to Saturday, newest on the LEFT so the
              current week is visible without scrolling. The strip is built from
              the durable per-day rollups as well as the rows, so a week older
              than the loaded slice is still there, with its true count. */}
          <div style={S.weekStrip} role="tablist" aria-label="Weeks">
            {routeWeeks.map((w) => {
              const on = w.key === selWeek;
              return (
                <button key={w.key} role="tab" aria-selected={on}
                        style={{ ...S.weekChip, ...(on ? S.weekChipOn : {}) }}
                        title={weekLabel(w.key, { long: true })}
                        onClick={() => { setRouteWeek(w.key); setOpenRoute(null); }}>
                  <span style={S.weekChipLabel}>{weekLabel(w.key)}</span>
                  <span style={{ ...S.weekChipCount, ...(on ? S.weekChipCountOn : {}) }}>
                    {w.journeys ? `${w.journeys} journey${w.journeys === 1 ? '' : 's'}` : `${w.searches} search${w.searches === 1 ? '' : 'es'}`}
                  </span>
                </button>
              );
            })}
          </div>

          <div style={S.weekHead}>
            <div style={S.weekTitle}>{selWeek ? weekLabel(selWeek, { long: true }) : 'No weeks recorded yet'}</div>
            <div style={S.sectionHint}>
              {weekGroups.length} journey{weekGroups.length === 1 ? '' : 's'} from{' '}
              {weekGroups.reduce((n, g) => n + g.tries.length, 0)} searches on screen
              {selWeekInfo.searches ? ` · ${selWeekInfo.searches} searches in the daily counters` : ''}
            </div>
          </div>

          {rowsMissing && (
            <div style={S.privacyNote}>
              This week is at or past the oldest search this screen loaded — the dashboard carries the most recent{' '}
              {routes.length.toLocaleString('en-CA')} of {(data?.routesTotal || 0).toLocaleString('en-CA')}.{' '}
              <button style={S.linkBtn} onClick={loadAllRoutes} disabled={allRoutesBusy}>
                {allRoutesBusy ? 'Loading every search…' : 'Load the full history'}
              </button>
            </div>
          )}

          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>When</th>
                  <th style={S.th}>From</th>
                  <th style={S.th}>To</th>
                  <th style={S.th}>Scan</th>
                  <th style={S.th}>Options</th>
                  <th style={S.th}>Found</th>
                  <th style={S.th}>Why not</th>
                  <th style={S.th}>Meters</th>
                </tr>
              </thead>
              <tbody>
                {weekGroups.map((g, i) => {
                  const r = g.latest;
                  const many = g.tries.length > 1;
                  const rowKey = `${g.pair}|${g.tries[g.tries.length - 1].ts}`;
                  const open = openRoute === rowKey;
                  // Whether the attempts actually differ. "3 preferences tried"
                  // is a claim about someone comparing routes; when every
                  // attempt carries the same toggles it is just the same search
                  // repeated, and saying so keeps the tags visible instead of
                  // hiding an unchanging set behind a dropdown.
                  const variedOptions = many &&
                    new Set(g.tries.map(t => optionTags(t.options))).size > 1;
                  return (
                    <Fragment key={rowKey}>
                      <tr style={i % 2 ? S.trAlt : undefined}>
                        <td style={S.td}>{fmtTime(r.ts)}</td>
                        <td style={S.td}>
                          {isOutdoorStart(r.from) ? (
                            // The app's outdoor auto-start: "Outdoors" says
                            // nothing about WHERE, so the footpath node it
                            // started from is one click behind it.
                            <>
                              <button style={S.linkBtn} onClick={() => toggleFromNode(rowKey)}
                                      aria-expanded={openFromNode.has(rowKey)}
                                      title="The outdoor nav node the route started from">
                                {openFromNode.has(rowKey) ? '▾' : '▸'} Outdoors
                              </button>
                              {openFromNode.has(rowKey) && (
                                <div style={{ ...S.mono, userSelect: 'all', marginTop: 2 }}>{r.from.nodeId}</div>
                              )}
                            </>
                          ) : (r.from?.text || '—')}
                        </td>
                        <td style={S.td}>{r.to ? `${r.to.name || r.to.building || ''} ${r.to.room || ''}`.trim() : '—'}</td>
                        <td style={S.td}>{r.scan || '—'}</td>
                        <td style={S.td}>
                          {many ? (
                            // The dropdown is where the sequence lives. What it
                            // is a sequence OF depends on whether the toggles
                            // moved: preferences compared, or the same search
                            // made again.
                            <button style={S.linkBtn} onClick={() => setOpenRoute(open ? null : rowKey)}>
                              {open ? '▾' : '▸'} {variedOptions
                                ? `${g.tries.length} preferences tried`
                                : `${optionTags(r.options)} · ×${g.tries.length}`}
                            </button>
                          ) : optionTags(r.options)}
                        </td>
                        <td style={S.td}>{g.found ? '✅' : '❌'}</td>
                        <td style={S.td}>{FAIL_LABELS[r.reason] || (g.found ? '—' : '')}</td>
                        <td style={S.td}>{g.meters != null ? g.meters : '—'}</td>
                      </tr>
                      {many && open && g.tries.map((t, k) => (
                        <tr key={`${i}-${k}`} style={S.trNested}>
                          <td style={{ ...S.td, ...S.tdNested }}>{fmtTime(t.ts)}</td>
                          <td style={S.td} colSpan={2}>
                            <span style={S.dim}>attempt {g.tries.length - k}</span>
                            {/* Attempts group on From's TEXT, so two outdoor
                                starts at different footpath nodes share a row;
                                the one the row reveals is the latest. An
                                attempt that began elsewhere says where. */}
                            {isOutdoorStart(t.from) && t.from.nodeId !== r.from?.nodeId && (
                              <span style={S.dim}> · from <span style={{ ...S.mono, userSelect: 'all' }}>{t.from.nodeId}</span></span>
                            )}
                          </td>
                          <td style={S.td} />
                          <td style={S.td}>{optionTags(t.options)}</td>
                          <td style={S.td}>{t.found ? '✅' : '❌'}</td>
                          <td style={S.td}>{FAIL_LABELS[t.reason] || (t.found ? '—' : '')}</td>
                          <td style={S.td}>{t.meters != null ? t.meters : '—'}</td>
                        </tr>
                      ))}
                    </Fragment>
                  );
                })}
                {weekGroups.length === 0 && (
                  <tr><td style={S.td} colSpan={8}>
                    {rowsMissing ? 'This week\'s rows are not loaded — see above.' : 'No route searches this week.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          </>
        )}

        {tab === 'mobile' && (
          <>
            <div style={S.counts}>
              <Stat label="Beta signups" value={mobile.total} hint="from /mobile, before the store release" />
              <Stat label="iPhone" value={mobile.ios.length} hint="TestFlight — link was public" />
              <Stat label="Android" value={mobile.android.length} />
              <Stat label="Waiting on you" value={mobile.waiting}
                    hint="not yet added in the Play Console"
                    tone={mobile.waiting > 0 ? 'warn' : undefined} />
              {/* Whether the app's share button actually brings anybody in —
                  the only question the referral store can answer, and the whole
                  reason it exists. It sits with the signups because a referral
                  IS a signup; it is not a tab of its own because there is
                  nothing to act on, only a number to read. */}
              <Stat label="Referred" value={referrals.referred}
                    hint={`signed up from a shared link · ${referrals.codes} code${referrals.codes === 1 ? '' : 's'} used`} />
            </div>

            {/* Codes and counts, and nothing else — see api/_referrals.js.
                Nothing here can say who a code belongs to, which is deliberate:
                the app publishes these into group chats, so a table that joined
                them to the students they brought in would be a linkage UMaps
                does not otherwise have.

                COLLAPSED BY DEFAULT, with the tally on the header itself: "how
                many has sharing brought in" is the whole question most visits
                ask, and the per-code list is the detail behind it. */}
            <div style={S.reportItem}>
              <button style={S.collapseHead} aria-expanded={referralsOpen}
                      onClick={() => setReferralsOpen((o) => !o)}>
                <span style={S.reportCaret}>{referralsOpen ? '▾' : '▸'}</span>
                <span style={S.collapseTitle}>Referrals</span>
                <span style={S.tally}>{referrals.referred}</span>
                <span style={S.reportWhen}>
                  student{referrals.referred === 1 ? '' : 's'} referred · {referrals.codes} code{referrals.codes === 1 ? '' : 's'} used
                </span>
              </button>
              {referralsOpen && (
                <div style={S.reportBody}>
                  {referrals.rows.length === 0 ? (
                    <div style={S.dim}>Nobody has signed up from a shared link yet.</div>
                  ) : (
                    <div style={S.referralGrid}>
                      {referrals.rows.map(r => (
                        <div key={r.code} style={S.referralRow}>
                          <span style={S.reportChip}>{r.code}</span>
                          <span style={S.reportWhen}>{r.count} student{r.count === 1 ? '' : 's'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={{ height: 12 }} />

            {/* ⚠️ TWO GROUPS BECAUSE THERE ARE TWO JOBS, and the difference is
                WHICH PEOPLE ARE MEANT. This used to be five buttons in two
                undifferentiated rows with a paragraph at the end, which meant
                the distinction lived only in the operator's head.

                "This round" acts on the SAME set in all three — the ones still
                waiting — which is why the count on all three is one number.
                Splitting it would let somebody email a block they had not
                added, or tick off a block they had not pasted.

                "Announcements" are not a round; nothing in them is a to-do.
                "The app is out" goes to every student who ever signed up; "the
                build is up" is closed-testing news that means nothing to
                somebody on TestFlight.

                Play Store addresses and school addresses are never one list: a
                Play Store address is usually a personal Gmail nobody has said
                we may write to, and a school address is no use to Google's
                allow-list. Both join on ", " — what the Testers box takes, and
                what a To: field takes. */}
            <ActionBar>
              <ActionGroup
                label={`This round · ${mobile.waiting} waiting`}
                note="Paste the Play Store addresses into Play Console → Testing → Closed testing → Testers, email their school addresses to say they are in, then mark them added."
              >
                <button
                  style={S.btnGhost}
                  disabled={mobile.waiting === 0}
                  onClick={() => {
                    const list = mobile.uninvited.map(r => r.playEmail).filter(Boolean).join(', ');
                    try { navigator.clipboard?.writeText(list); } catch { /* ignore */ }
                  }}
                >Copy {mobile.waiting} Play Store address{mobile.waiting === 1 ? '' : 'es'}</button>
                <button
                  style={S.btnGhost}
                  disabled={mobile.waiting === 0}
                  onClick={() => {
                    const list = mobile.uninvited.map(r => r.email).filter(Boolean).join(', ');
                    try { navigator.clipboard?.writeText(list); } catch { /* ignore */ }
                  }}
                >Copy their {mobile.waiting} school email{mobile.waiting === 1 ? '' : 's'}</button>
                {/* Red, like the per-row "Mark added" buttons it stands in for —
                    the same act in one tap, so it must not read as a third copy
                    button sitting beside two. */}
                <button
                  style={S.btnDanger}
                  disabled={mobile.waiting === 0 || mobileBusy === 'all'}
                  onClick={() => markAllInvited(mobile.uninvited)}
                >{mobileBusy === 'all' ? 'Marking…' : `Mark all ${mobile.waiting} as added`}</button>
              </ActionGroup>

              <ActionGroup
                label="Announcements"
                note="Everybody, or every Android tester for a new closed-testing build. Neither is a to-do."
              >
                <button
                  style={S.btnGhost}
                  disabled={mobile.students.length === 0}
                  onClick={() => {
                    try { navigator.clipboard?.writeText(mobile.students.join(', ')); } catch { /* ignore */ }
                  }}
                >All {mobile.students.length} student email{mobile.students.length === 1 ? '' : 's'}</button>
                <button
                  style={S.btnGhost}
                  disabled={mobile.android.length === 0}
                  onClick={() => {
                    const list = mobile.android.map(r => r.email).filter(Boolean).join(', ');
                    try { navigator.clipboard?.writeText(list); } catch { /* ignore */ }
                  }}
                >All {mobile.android.length} Android school email{mobile.android.length === 1 ? '' : 's'}</button>
              </ActionGroup>

              <ExportGroup tab="mobile" data={data} />
            </ActionBar>

            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>When</th>
                    <th style={S.th}>Platform</th>
                    <th style={S.th}>School email</th>
                    <th style={S.th}>Play Store email</th>
                    <th style={S.th}>Added as tester</th>
                  </tr>
                </thead>
                <tbody>
                  {mobile.rows.map((r, i) => (
                    <tr key={`${r.platform}-${r.email}`} style={i % 2 ? S.trAlt : undefined}>
                      <td style={S.td}>{fmtTime(r.ts)}</td>
                      <td style={S.td}>{r.platform === 'ios' ? 'iPhone' : 'Android'}</td>
                      <td style={S.td}>{r.email}</td>
                      {/* An iOS row has no second address, and an em dash says
                          so better than a blank cell that reads as missing. */}
                      <td style={S.td}>{r.playEmail || <span style={S.dim}>—</span>}</td>
                      <td style={S.td}>
                        {r.platform === 'ios'
                          ? <span style={S.dim}>not needed</span>
                          : (
                            <button
                              style={r.invited ? S.btnGhost : S.btnDanger}
                              disabled={mobileBusy === r.email}
                              onClick={() => setInvited(r, !r.invited)}
                            >
                              {mobileBusy === r.email ? '…' : r.invited ? '✓ Added' : 'Mark added'}
                            </button>
                          )}
                      </td>
                    </tr>
                  ))}
                  {mobile.rows.length === 0 && (
                    <tr><td style={S.td} colSpan={5}>
                      Nobody has signed up yet. They appear here as soon as someone submits the
                      form at <code>/mobile</code>.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* The public changelog. The only tab that WRITES something students
            read, which is why it lives in its own file and why it is handed the
            reports and the ratings: an entry is worth much more when it carries
            the complaint it answers, and the operator should not have to copy
            that across from another tab by hand. */}
        {tab === 'changelog' && (
          <ChangelogAdmin
            token={token}
            posts={changelog}
            reports={reports}
            ratings={ratings}
            onChanged={loadChangelog}
          />
        )}

        {/* The final exam timetable, synced from the registrar daily and
            pasted by hand when that page changes shape. See docs/FINAL_EXAMS.md. */}
        {tab === 'finalExams' && <FinalExamsAdmin token={token} />}

        {/* How often the app's Safety button and each of its numbers are used. */}
        {tab === 'safety' && <SafetyAdmin token={token} />}

        {/* Remote popups: written here, signed by the server, shown by the app.
            See docs/POPUPS.md. */}
        {tab === 'popups' && (
          <PopupsAdmin
            token={token}
            onExpired={() => { saveToken(''); setData(null); setErr('Session expired — log in again.'); }}
            onSeenAttempts={() => setPopupsAlert(false)}
          />
        )}

        {/* The campus shuttle: mode, the tracker phone's status, its controls,
            and the leg timings it has produced. See api/_shuttle.js. */}
        {tab === 'shuttle' && (
          <ShuttleAdmin
            token={token}
            onExpired={() => { saveToken(''); setData(null); setErr('Session expired — log in again.'); }}
          />
        )}

        {/* The Nearby panel: what people look for when they want somewhere to
            sit, or a washroom. Counts only — see api/_placeLog.js for what is
            deliberately not kept. */}
        {tab === 'nearby' && (
          <>
            <ActionBar><ExportGroup tab="nearby" data={data} /></ActionBar>
            <div style={S.counts}>
              <Stat label="Panel opened" value={nearby.opens} />
              <Stat label="Places opened" value={nearby.views} />
              <Stat label="Took directions" value={nearby.goPct != null ? `${nearby.goPct}%` : '—'}
                    hint={`${nearby.gos} of ${nearby.views}`} />
              <Stat label="Sessions" value={nearby.sessions} hint="distinct visits that used Nearby" />
              <Stat label="Searched, found nothing" value={nearby.empties}
                    hint="a filter combination with no match"
                    tone={nearby.empties > 0 ? 'warn' : undefined} />
            </div>

            <div style={S.privacyNote}>
              Counts only, and less than the route log keeps: these rows carry no session
              id at all, so no one can reconstruct which washrooms a single visitor looked
              at. Unique visits are counted against a per-day list of throwaway ids that
              expires within 48h. No location, no accounts, nothing joined to the email list.
            </div>

            <div style={S.sectionTitle}>What people are looking for</div>
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead><tr>
                  <th style={S.th}>Kind</th><th style={S.th}>Opened</th>
                  <th style={S.th}>Share</th><th style={S.th}>Found nothing</th>
                </tr></thead>
                <tbody>
                  {nearby.kinds.map((k, i) => (
                    <tr key={k.kind} style={i % 2 ? S.trAlt : undefined}>
                      <td style={S.td}>{KIND_LABELS[k.kind] || k.kind}</td>
                      <td style={S.td}>{k.views}</td>
                      <td style={S.td}>{nearby.sharePct(k.views) != null ? `${nearby.sharePct(k.views)}%` : '—'}</td>
                      <td style={S.td}>{k.empty || 0}</td>
                    </tr>
                  ))}
                  {nearby.kindTotal === 0 && (
                    <tr><td style={S.td} colSpan={4}>Nothing recorded yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* GROUPED BY KIND — a heading row per kind with its subtotal, the
                places under it most-viewed first. The question is always asked
                about one kind ("which library"), and one flat ranking buried
                the libraries under a hundred washrooms. */}
            <div style={S.sectionTitle}>Most-looked-at places, by kind</div>
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead><tr>
                  <th style={S.th}>Place</th>
                  <th style={S.th}>Opened</th><th style={S.th}>Directions taken</th>
                </tr></thead>
                <tbody>
                  {nearby.placeGroups.map((g) => (
                    <Fragment key={g.kind || 'none'}>
                      <tr style={S.groupRow}>
                        <td style={{ ...S.td, ...S.groupCell }}>
                          {g.label} <span style={S.dim}>· {g.places.length} place{g.places.length === 1 ? '' : 's'}</span>
                        </td>
                        <td style={{ ...S.td, ...S.groupCell }}>{g.views}</td>
                        <td style={{ ...S.td, ...S.groupCell }}>{g.gos}</td>
                      </tr>
                      {g.places.map((p, i) => (
                        <tr key={p.placeId} style={i % 2 ? S.trAlt : undefined}>
                          <td style={{ ...S.td, ...S.tdIndent }}>{p.name || p.placeId}</td>
                          <td style={S.td}>{p.views}</td>
                          <td style={S.td}>{p.gos}</td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                  {nearby.places.length === 0 && (
                    <tr><td style={S.td} colSpan={3}>No places opened yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div style={S.sectionTitle}>Day by day</div>
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead><tr>
                  <th style={S.th}>Day</th><th style={S.th}>Sessions</th><th style={S.th}>Opened panel</th>
                  <th style={S.th}>Study</th><th style={S.th}>Libraries</th><th style={S.th}>Washrooms</th>
                  <th style={S.th}>Directions</th><th style={S.th}>Found nothing</th>
                </tr></thead>
                <tbody>
                  {nearby.days.map((d, i) => (
                    <tr key={d.day} style={i % 2 ? S.trAlt : undefined}>
                      <td style={S.td}>{d.day}</td>
                      <td style={S.td}>{d.sessions || '—'}</td>
                      <td style={S.td}>{d['ev:open'] || 0}</td>
                      <td style={S.td}>{d['kind:study'] || 0}</td>
                      <td style={S.td}>{d['kind:library'] || 0}</td>
                      <td style={S.td}>{d['kind:bathroom'] || 0}</td>
                      <td style={S.td}>{d['ev:go'] || 0}</td>
                      <td style={S.td}>{d['ev:empty'] || 0}</td>
                    </tr>
                  ))}
                  {nearby.days.length === 0 && (
                    <tr><td style={S.td} colSpan={8}>
                      No days recorded yet — this fills up as people use the Nearby panel.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === 'eventUse' && (
          <>
            <ActionBar><ExportGroup tab="eventUse" data={data} /></ActionBar>
            <div style={S.counts}>
              <Stat label="Events opened" value={eventUse.opens}
                    hint={`${eventUse.pins} from a map pin, ${eventUse.rows} from the calendar`} />
              <Stat label="Asked for directions" value={eventUse.goPct != null ? `${eventUse.goPct}%` : '—'}
                    hint={`${eventUse.directions} of ${eventUse.opens}`} />
              <Stat label="Found from the map" value={eventUse.pinPct != null ? `${eventUse.pinPct}%` : '—'}
                    hint="the rest came from the Events calendar" />
              <Stat label="Starred" value={eventUse.saves}
                    hint={eventUse.unsaves ? `${eventUse.unsaves} later un-starred` : 'none un-starred'} />
              <Stat label="Sessions" value={eventUse.sessions} hint="distinct visits that opened an event" />
            </div>

            <div style={S.privacyNote}>
              Counts only, and less than the route log keeps: these rows carry no session
              id at all, so nobody can reconstruct which clubs one student looked at, one
              after another. Unique visits are counted against a per-day list of throwaway
              ids that expires within 48h. No location, no accounts, nothing joined to the
              email list — and nothing here is tied to the club's own signup figure, which
              is a number the club types and we never count.
            </div>

            {/* ⚠️ THE PER-EVENT TABLE IS NOT HERE ANY MORE — it is on each
                event's own card in /eventmapcreator, where the club that made
                the event can read it. It sat here for a while, which meant the
                one group with a reason to act on the numbers was the only group
                that could not see them. What is left is the half no club can
                act on and no per-event view can show: whether people arrive by
                pin or by calendar, how many distinct visits opened anything at
                all, and one week against another. The operator still sees every
                event's figures — they see every club's cards in the creator. */}
            <div style={S.privacyNote}>
              Per-event numbers moved to <a href="/eventmapcreator" style={S.noteLink}>the event
              creator</a>, on each event's card, so the club that made it can see them.
              This tab is the event layer as a whole.
            </div>

            <div style={S.sectionTitle}>Day by day</div>
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead><tr>
                  <th style={S.th}>Day</th><th style={S.th}>Sessions</th><th style={S.th}>Opened</th>
                  <th style={S.th}>From a pin</th><th style={S.th}>From the list</th>
                  <th style={S.th}>Directions</th><th style={S.th}>Starred</th>
                  <th style={S.th}>Tickets</th><th style={S.th}>Club pages</th>
                </tr></thead>
                <tbody>
                  {eventUse.days.map((d, i) => (
                    <tr key={d.day} style={i % 2 ? S.trAlt : undefined}>
                      <td style={S.td}>{d.day}</td>
                      <td style={S.td}>{d.sessions || '—'}</td>
                      <td style={S.td}>{d.opens || 0}</td>
                      <td style={S.td}>{d['ev:pin'] || 0}</td>
                      <td style={S.td}>{d['ev:row'] || 0}</td>
                      <td style={S.td}>{d['ev:directions'] || 0}</td>
                      <td style={S.td}>{d['ev:save'] || 0}</td>
                      <td style={S.td}>{d['ev:tickets'] || 0}</td>
                      <td style={S.td}>{d['ev:club'] || 0}</td>
                    </tr>
                  ))}
                  {eventUse.days.length === 0 && (
                    <tr><td style={S.td} colSpan={9}>
                      No days recorded yet — this fills up as people open events.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === 'downloads' && (
          <>
            <div style={S.counts}>
              <Stat label="Downloads" value={downloads.total}
                    hint={`${rangeLabel({ from: APP_RELEASE_DAY, to: null }).toLowerCase()} — the store release`} />
              <Stat label="iPhone" value={downloads.ios} />
              <Stat label="Android" value={downloads.android} />
              {/* Shown only when it is non-zero: a phone that sent no platform,
                  or the hours of release day before the split was deployed.
                  Kept so iPhone + Android + this is always the total. */}
              {downloads.unknown > 0 && (
                <Stat label="Platform not recorded" value={downloads.unknown}
                      hint="counted, but before the split was recorded" />
              )}
              <Stat label="Today" value={downloads.today} />
              <Stat label="Best day" value={downloads.best.value}
                    hint={downloads.best.day ? downloads.best.day : 'nothing yet'} />
            </div>

            <div style={S.privacyNote}>
              A download is a phone opening the released app for the first time — the app sends
              its first-run answers once, and each distinct phone is counted on the day they
              arrive. Counted from {rangeLabel({ from: APP_RELEASE_DAY, to: APP_RELEASE_DAY }).split(' – ')[0]},
              the day UMaps went on the App Store and Google Play and every beta phone had to
              download it again. It is not the stores&rsquo; own figure, and a phone that turned
              analytics off in Settings is not in it, so read it as a floor. The beta&rsquo;s
              numbers are on App questions and Beta signups.
            </div>

            <ActionBar><ExportGroup tab="downloads" data={data} /></ActionBar>

            <div style={S.chartGrid}>
              <div style={S.chartWide}>
                <ChartCard
                  title="Downloads since release"
                  hint={`Running total of distinct phones, over ${downloads.dayCount} day${downloads.dayCount === 1 ? '' : 's'}. From the per-day counters, which never expire.`}
                  empty={downloads.total ? null : 'Nothing yet — the line starts with the first phone to open the released app.'}
                >
                  <TrendChart points={downloads.series} cumulative label="Total downloads" />
                </ChartCard>
              </div>

              <div style={S.chartWide}>
                <ChartCard
                  title="Downloads each day"
                  hint="Distinct phones per day — the same counters, not accumulated. A day with none is drawn as zero."
                  empty={downloads.total ? null : 'Nothing recorded yet.'}
                >
                  <TrendChart points={downloads.series} label="Downloads per day" />
                </ChartCard>
              </div>

              <ChartCard
                title="Phone"
                hint="Share of downloads since release, by platform."
                empty={downloads.total ? null : 'Nothing recorded yet.'}
              >
                <DonutChart slices={[
                  { label: 'iPhone', value: downloads.ios },
                  { label: 'Android', value: downloads.android },
                  ...(downloads.unknown ? [{ label: 'Not recorded', value: downloads.unknown }] : []),
                ]} unit="phones" />
              </ChartCard>
            </div>

            <div style={S.sectionTitle}>By day</div>
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead><tr>
                  <th style={S.th}>Day</th><th style={S.th}>Downloads</th>
                  <th style={S.th}>iPhone</th><th style={S.th}>Android</th>
                  <th style={S.th}>Running total</th>
                </tr></thead>
                <tbody>
                  {downloads.rows.map((r, i) => (
                    <tr key={r.day} style={i % 2 ? S.trAlt : undefined}>
                      <td style={S.td}>{r.day}</td>
                      <td style={S.td}>{r.value}</td>
                      <td style={S.td}>{r.ios}</td>
                      <td style={S.td}>{r.android}</td>
                      <td style={S.td}>{r.running}</td>
                    </tr>
                  ))}
                  {downloads.rows.length === 0 && (
                    <tr><td style={S.td} colSpan={5}>No days recorded yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === 'appProfile' && (
          <>
            <div style={S.counts}>
              <Stat label="Questionnaires" value={appProfile.answered}
                    hint="one per install, latest answer" />
              <Stat label="iOS" value={appProfile.ios} />
              <Stat label="Android" value={appProfile.android} />
              <Stat label="First years" value={appProfile.firstYear}
                    hint={appProfile.pct(appProfile.firstYear) != null ? `${appProfile.pct(appProfile.firstYear)}% of answers` : undefined} />
              <Stat label="Accessible by default" value={appProfile.accessible}
                    hint={appProfile.pct(appProfile.accessible) != null ? `${appProfile.pct(appProfile.accessible)}% of answers` : undefined} />
              <Stat label="Entered a timetable" value={appProfile.withCourses} />
            </div>

            <div style={S.privacyNote}>
              The app's five first-run questions, counted. No courses, no timetable, no
              location, no account, no advertising identifier — the only identifier stored is
              a random install id, and it exists so that editing an answer in Settings
              corrects your earlier one instead of counting you twice. Answering is optional
              and every question is skippable, which is why "Skipped" is a row rather than a
              gap. It can be turned off entirely in the app's Settings.
            </div>

            <ActionBar><ExportGroup tab="appProfile" data={data} /></ActionBar>

            {/* ⚠️ EACH CHART NAMES ITS OWN SOURCE, because they are not the
                same source and the difference is invisible once it is a
                picture. The trend and the shares come from counter hashes that
                never expire; the by-hour chart is the only one drawn from the
                raw rows, which are a capped, one-year sample — the sole record
                carrying a timestamp precise enough for an hour. A reader who
                assumes all four go back equally far draws the wrong conclusion
                from the shortest one. See chartData() above. */}
            <div style={S.chartGrid}>
              <div style={S.chartWide}>
                <ChartCard
                  title="Signups over time"
                  hint={appProfile.trendDays
                    ? `Running total of distinct phones that answered, over ${appProfile.trendDays} day${appProfile.trendDays === 1 ? '' : 's'}. From the per-day counters, which never expire.`
                    : undefined}
                  empty={appProfile.trendDays ? null
                    : 'No days recorded yet — the line starts with the next questionnaire.'}
                >
                  <TrendChart points={appProfile.trendInstalls} cumulative label="Total signups" />
                </ChartCard>
              </div>

              <div style={S.chartWide}>
                <ChartCard
                  title="New signups each day"
                  hint="Distinct phones per day — the same counters, not accumulated."
                  empty={appProfile.trendDays ? null : 'Nothing recorded yet.'}
                >
                  <TrendChart points={appProfile.trendInstalls} label="Signups per day" />
                </ChartCard>
              </div>

              <div style={S.chartWide}>
                <ChartCard
                  title="What time of day people answer"
                  hint={appProfile.hoursFrom
                    ? `Local time, from the ${appProfile.hoursFrom.toLocaleString('en-CA')} most recent answers — the only source with a time on it, and a capped sample rather than all time.`
                    : undefined}
                  empty={appProfile.hoursFrom ? null : 'No answers recorded yet.'}
                >
                  <HourChart values={appProfile.hours} />
                </ChartCard>
              </div>

              <ChartCard
                title="Year of study"
                hint="Share of everyone who answered. Counted once per install."
                empty={appProfile.answered ? null : 'No questionnaires yet.'}
              >
                <DonutChart slices={appProfile.yearSlices} />
              </ChartCard>

              <ChartCard
                title="Faculty"
                hint="Top six, with the rest kept as one slice so the shares still add up to everybody. The full list is in the table below."
                empty={appProfile.answered ? null : 'No questionnaires yet.'}
              >
                <DonutChart slices={appProfile.facultySlices} />
              </ChartCard>

              <ChartCard
                title="Phone"
                empty={appProfile.answered ? null : 'No questionnaires yet.'}
              >
                <DonutChart slices={appProfile.platformSlices} />
              </ChartCard>

              <ChartCard
                title="Accessible routes and washrooms"
                hint="What the accessible option defaults to for that many people — the one answer here that changes what the app does."
                empty={appProfile.answered ? null : 'No questionnaires yet.'}
              >
                <DonutChart slices={appProfile.accessibleSlices} />
              </ChartCard>
            </div>

            <div style={S.twoCol}>
              <div>
                <div style={S.sectionTitle}>Year</div>
                <div style={S.tableWrap}>
                  <table style={S.table}>
                    <thead><tr>
                      <th style={S.th}>Year</th><th style={S.th}>People</th><th style={S.th}>Share</th>
                    </tr></thead>
                    <tbody>
                      {appProfile.years.map((y, i) => (
                        <tr key={y.key} style={i % 2 ? S.trAlt : undefined}>
                          <td style={S.td}>{y.label}</td>
                          <td style={S.td}>{y.n}</td>
                          <td style={S.td}>{appProfile.pct(y.n) != null ? `${appProfile.pct(y.n)}%` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <div style={S.sectionTitle}>Commuter overlays</div>
                <div style={S.tableWrap}>
                  <table style={S.table}>
                    <thead><tr>
                      <th style={S.th}>Overlay</th><th style={S.th}>Kept on</th><th style={S.th}>Turned off</th>
                    </tr></thead>
                    <tbody>
                      {appProfile.commutes.map((c, i) => (
                        <tr key={c.key} style={i % 2 ? S.trAlt : undefined}>
                          <td style={S.td}>{c.label}</td>
                          <td style={S.td}>{c.on}</td>
                          <td style={S.td}>{c.off}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* The one number on this tab that is not a preference: it is
                    what the accessible route option and the washroom filter
                    default to for that many people. */}
                <div style={S.sectionTitle}>Accessible routes &amp; washrooms</div>
                <div style={S.tableWrap}>
                  <table style={S.table}>
                    <thead><tr><th style={S.th}>Answer</th><th style={S.th}>People</th></tr></thead>
                    <tbody>
                      <tr><td style={S.td}>On</td><td style={S.td}>{appProfile.accessible}</td></tr>
                      <tr style={S.trAlt}><td style={S.td}>Off</td><td style={S.td}>{appProfile.accessibleNo}</td></tr>
                      <tr><td style={S.td}>Skipped</td><td style={S.td}>{appProfile.accessibleSkipped}</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div style={S.sectionTitle}>Faculty</div>
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead><tr>
                  <th style={S.th}>Faculty, college or school</th>
                  <th style={S.th}>People</th><th style={S.th}>Share</th>
                </tr></thead>
                <tbody>
                  {appProfile.faculties.map((f, i) => (
                    <tr key={f.key} style={i % 2 ? S.trAlt : undefined}>
                      <td style={S.td}>{f.label}</td>
                      <td style={S.td}>{f.n}</td>
                      <td style={S.td}>{appProfile.pct(f.n) != null ? `${appProfile.pct(f.n)}%` : '—'}</td>
                    </tr>
                  ))}
                  {appProfile.answered === 0 && (
                    <tr><td style={S.td} colSpan={3}>No questionnaires recorded yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

      </div>
    </div>
  );
}

/** One headline number, big enough to read off a projector. */
function Stat({ label, value, hint, tone }) {
  return (
    <div style={{ ...S.stat, ...(tone === 'warn' ? S.statWarn : {}) }}>
      <div style={S.statValue}>{value}</div>
      <div style={S.statLabel}>{label}</div>
      {hint && <div style={S.statHint}>{hint}</div>}
    </div>
  );
}

/**
 * A named group of buttons that act on one thing.
 *
 * ⚠️ THE LABEL IS THE POINT, not decoration. Before this the App-signups tab
 * put five buttons in two undifferentiated rows, and the two rows meant
 * genuinely different things — one round of work on the people still waiting,
 * and two announcements to everybody — with nothing on screen saying so. An
 * operator reading five grey buttons of the same size had to remember which
 * ones acted on which set of people, and getting it wrong means emailing a
 * block nobody has added yet.
 *
 * `note` is the explanation for the whole group, which is where the loose
 * paragraph that used to sit at the end of a button row belongs.
 */
function ActionGroup({ label, note, children }) {
  return (
    <div style={S.group}>
      <div style={S.groupLabel}>{label}</div>
      <div style={S.groupButtons}>{children}</div>
      {note && <div style={S.groupNote}>{note}</div>}
    </div>
  );
}

/** A row of `ActionGroup`s, wrapping on a narrow screen. */
const ActionBar = ({ children }) => <div style={S.actionBar}>{children}</div>;

/**
 * The download control, in ONE place on every tab that has one.
 *
 * Before this the exports were wherever they had been added — a "Download CSV"
 * among the copy buttons on App signups, another beside "Copy all emails", and
 * nothing at all on the seven other tabs that hold data. Same act, three
 * different places, seven missing. Now every tab's download sits in the same
 * spot with the same words, and the tabs that deliberately have none say why.
 */
function ExportGroup({ tab, data, hydrate }) {
  // Declared before the early return below: a hook after a conditional `return`
  // is called on some renders and not others, which is the one rule hooks have.
  const [working, setWorking] = useState('');
  if (!canExport(tab)) {
    return (
      <ActionGroup
        label="Download"
        note="This page has no spreadsheet — Analytics → Download data has every table."
      >
        <button style={{ ...S.btnGhost, ...S.btnOff }} disabled>Not available here</button>
      </ActionGroup>
    );
  }
  const rows = exportRowCount(tab, data);
  const sheets = pageSheetCount(tab, data);
  // ⚠️ This page holds more than the screen does, so the file takes a moment to
  // assemble and the button has to SAY it is working. Without that, the gap
  // between the click and the save dialog reads as nothing having happened,
  // and the second click starts a second download of thirty thousand rows.
  const more = pageNeedsAll(tab) && rows > (data?.routes || []).length;
  const run = async (kind, fn) => {
    setWorking(kind);
    try { await fn(tab, data, hydrate); } finally { setWorking(''); }
  };
  return (
    <ActionGroup
      label="Download"
      note={more
        ? `The screen shows the most recent ${(data?.routes || []).length.toLocaleString('en-CA')}; the file has all `
          + `${rows.toLocaleString('en-CA')}, fetched when you press it.`
        : sheets > 1
          // Said plainly, because the difference decides which button to press:
          // a CSV holds one table and this page is several.
          ? `This page is ${sheets} tables. The workbook keeps them on separate sheets; the CSV is the first one only.`
          : 'Excel and Sheets both open either.'}
    >
      <button style={S.btnGhost} disabled={!rows || !!working} onClick={() => run('xlsx', downloadPageXlsx)}>
        {working === 'xlsx'
          ? 'Collecting…'
          : `Download this page (${rows.toLocaleString('en-CA')} row${rows === 1 ? '' : 's'})`}
      </button>
      <button style={S.btnGhost} disabled={!rows || !!working} onClick={() => run('csv', downloadPageCsv)}>
        {working === 'csv' ? 'Collecting…' : 'CSV'}
      </button>
    </ActionGroup>
  );
}

const S = {
  page: { minHeight: '100vh', background: '#f1f3f4', color: '#202124', fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif', padding: 16, boxSizing: 'border-box' },
  wrap: { maxWidth: 1000, margin: '0 auto' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  headerActions: { display: 'flex', gap: 8 },
  h1: { fontSize: 22, fontWeight: 800, display: 'flex', alignItems: 'center' },
  muted: { color: '#5f6368', fontSize: 14, margin: '6px 0 12px' },
  loginCard: { maxWidth: 340, margin: '12vh auto 0', background: '#fff', padding: 24, borderRadius: 16, boxShadow: '0 6px 28px rgba(0,0,0,0.14)', display: 'flex', flexDirection: 'column' },
  input: { fontSize: 16, padding: '11px 13px', border: '1.5px solid #dadce0', borderRadius: 10, outline: 'none', marginBottom: 12 },
  err: { background: '#fce8e6', color: '#c5221f', borderRadius: 8, padding: '8px 11px', fontSize: 13, marginBottom: 12 },
  btnPrimary: { background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 10, padding: 12, fontSize: 15, fontWeight: 800, cursor: 'pointer' },
  btnGhost: { background: '#fff', color: '#202124', border: '1.5px solid #dadce0', borderRadius: 8, padding: '8px 14px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  // Red only for the button that destroys the durable record. The other clear
  // is ordinary housekeeping and should not look like a hazard.
  btnDanger: { background: '#fff', color: '#c5221f', border: '1.5px solid #f3b8b4', borderRadius: 8, padding: '8px 14px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  toolbar: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '0 0 12px' },

  // ── Grouped actions ───────────────────────────────────────────────────────
  // One card per group of buttons that act on one thing, so "which of these
  // touches which people" is answered by the layout instead of by memory.
  actionBar: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start', margin: '0 0 12px' },
  group: {
    background: '#fff', border: '1px solid #dadce0', borderRadius: 12,
    padding: '9px 12px 11px', flex: '1 1 260px', minWidth: 0,
  },
  groupLabel: {
    fontSize: 11, fontWeight: 800, color: '#5f6368', textTransform: 'uppercase',
    letterSpacing: '0.05em', marginBottom: 7,
  },
  groupButtons: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  groupNote: { fontSize: 12, color: '#5f6368', lineHeight: 1.45, marginTop: 8 },
  // A disabled button that is disabled for a REASON given beside it, rather
  // than one waiting for data.
  btnOff: { color: '#80868b', cursor: 'default' },

  // ── Tab groups ────────────────────────────────────────────────────────────
  tabBar: { display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 },
  tabGroup: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' },
  tabGroupLabel: {
    fontSize: 10.5, fontWeight: 800, color: '#80868b', textTransform: 'uppercase',
    letterSpacing: '0.06em', marginRight: 2,
  },

  chartGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: 12, marginBottom: 12,
  },
  chartWide: { gridColumn: '1 / -1' },

  tabs: { display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' },
  tab: { background: '#fff', border: '1.5px solid #dadce0', borderRadius: 999, padding: '8px 16px', fontSize: 14, fontWeight: 700, cursor: 'pointer', color: '#202124' },
  tabOn: { background: '#1a73e8', borderColor: '#1a73e8', color: '#fff' },
  twoCol: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginBottom: 4 },
  sectionTitle: { fontSize: 12, fontWeight: 800, color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '14px 0 8px' },
  // No `marginLeft: auto` any more — it used to shove this to the far end of a
  // single flat row, which is what "archived" had to mean when every tab was in
  // one line. The groups say it now, so the nudge would only push it to the
  // right of its own group and read as a stray.
  tabArchived: { color: '#5f6368', fontWeight: 600 },
  tabDot: { display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#d93025', marginLeft: 7, verticalAlign: 'middle' },
  sectionHead: { display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', fontSize: 15, fontWeight: 800, margin: '4px 0 10px' },
  sectionHint: { fontSize: 12.5, fontWeight: 500, color: '#5f6368' },
  photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 12, marginBottom: 20 },
  photoCard: { background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column' },
  photoLink: { display: 'block' },
  photoImg: { display: 'block', width: '100%', height: 150, objectFit: 'cover', background: '#f1f3f4' },
  photoBroken: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, color: '#5f6368', textAlign: 'center', padding: 8, boxSizing: 'border-box' },
  photoMeta: { padding: '9px 11px 4px', flex: 1 },
  photoTitle: { fontSize: 13.5, fontWeight: 700, marginBottom: 2 },
  photoSub: { fontSize: 12, color: '#5f6368' },
  photoActions: { display: 'flex', gap: 8, padding: '8px 11px 11px' },
  btnAccept: { flex: 1, background: '#188038', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 10px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnReject: { flex: 1, background: '#fff', color: '#c5221f', border: '1.5px solid #f3c4c1', borderRadius: 8, padding: '8px 10px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  approvedGroup: { marginBottom: 8 },
  stat: { background: '#fff', border: '1px solid #dadce0', borderRadius: 12, padding: '11px 15px', minWidth: 118 },
  statWarn: { borderColor: '#f3c4c1', background: '#fff8f7' },
  statValue: { fontSize: 22, fontWeight: 800, lineHeight: 1.15 },
  statLabel: { fontSize: 12.5, color: '#3c4043', fontWeight: 600, marginTop: 2 },
  statHint: { fontSize: 11.5, color: '#5f6368', marginTop: 2 },
  privacyNote: { background: '#e8f0fe', color: '#174ea6', border: '1px solid #d2e3fc', borderRadius: 8, padding: '9px 12px', fontSize: 12.5, lineHeight: 1.45, marginBottom: 12 },
  noteLink: { color: 'inherit', fontWeight: 700 },
  archiveNote: { background: '#fef7e0', color: '#7a5900', border: '1px solid #fde293', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 12, lineHeight: 1.4 },
  counts: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 },
  countPill: { background: '#fff', border: '1px solid #dadce0', borderRadius: 999, padding: '6px 12px', fontSize: 13 },
  tableWrap: { background: '#fff', borderRadius: 12, overflow: 'auto', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e8eaed', color: '#5f6368', fontWeight: 700, whiteSpace: 'nowrap', position: 'sticky', top: 0, background: '#fff' },
  td: { padding: '9px 12px', borderBottom: '1px solid #f1f3f4', verticalAlign: 'top' },
  toolGrid: { display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', marginTop: 4 },
  toolCard: {
    display: 'block', background: '#fff', borderRadius: 14, padding: '16px 16px 13px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)', textDecoration: 'none', color: '#202124',
    border: '1.5px solid transparent', cursor: 'pointer',
  },
  toolIcon: { fontSize: 26, lineHeight: 1, marginBottom: 8 },
  toolName: { fontSize: 15.5, fontWeight: 800, marginBottom: 3 },
  toolDesc: { fontSize: 13, color: '#5f6368', lineHeight: 1.45, marginBottom: 8 },
  toolGate: { fontSize: 11, fontWeight: 700, color: '#80868b', textTransform: 'uppercase', letterSpacing: '0.04em' },
  // A count on the launcher card for work waiting on the operator; red like
  // the tab dot it replaces, so the card says it before it is opened.
  toolBadge: { display: 'inline-block', marginLeft: 8, background: '#fce8e6', color: '#c5221f', borderRadius: 999, padding: '2px 9px', fontSize: 11.5, fontWeight: 800, verticalAlign: 'middle', whiteSpace: 'nowrap' },
  backBtn: {
    background: '#fff', border: '1.5px solid #dadce0', borderRadius: 8, width: 30, height: 30,
    fontSize: 15, fontWeight: 800, cursor: 'pointer', marginRight: 10, color: '#202124',
  },
  trAlt: { background: '#fafbfc' },
  // The attempts behind a collapsed route row. Indented and tinted so the eye
  // reads them as belonging to the row above rather than as more journeys.
  trNested: { background: '#f6f8fb' },
  tdNested: { paddingLeft: 26, borderLeft: '3px solid #d2e3fc' },
  linkBtn: { background: 'none', border: 'none', padding: 0, font: 'inherit', color: '#1a73e8', fontWeight: 700, cursor: 'pointer' },
  dim: { color: '#80868b' },
  mono: { fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace', fontSize: 12 },
  reportList: { display: 'flex', flexDirection: 'column', gap: 8 },
  reportEmpty: { background: '#fff', borderRadius: 12, padding: 16, fontSize: 14, color: '#5f6368' },
  reportItem: { background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden' },
  // ⚠️ `boxSizing` is load-bearing: `width: 100%` plus 14px of horizontal
  // padding, with no global reset on this page, makes the row 28px WIDER than
  // the card it sits in — and the card is `overflow: hidden`, so the last 28px
  // are silently cut off. It only ever cost the Reports tab a few characters of
  // an ellipsised message; the moment the ratings row put a button at that edge
  // it started clipping the button.
  reportHead: { display: 'flex', alignItems: 'center', gap: 10, width: '100%', boxSizing: 'border-box', padding: '11px 14px', border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 13 },
  reportCaret: { color: '#9aa0a6', flexShrink: 0, width: 12 },
  reportWhen: { color: '#5f6368', whiteSpace: 'nowrap', flexShrink: 0 },
  reportChip: { background: '#f1f3f4', borderRadius: 999, padding: '3px 9px', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 },
  reportBadge: { background: '#fce8e6', color: '#c5221f', borderRadius: 999, padding: '3px 9px', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0, fontSize: 12 },
  reportSnippet: { color: '#202124', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 },
  reportBody: { borderTop: '1px solid #f1f3f4', padding: '12px 14px' },
  reportMsg: { fontSize: 14, color: '#202124', whiteSpace: 'pre-wrap', marginBottom: 12, lineHeight: 1.4 },
  reportGrid: { display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '6px 14px', fontSize: 13, color: '#3c4043', alignItems: 'start' },
  reportUrl: { wordBreak: 'break-all', color: '#5f6368' },

  // ── Admin Revamp ────────────────────────────────────────────────────────────
  tabLead: { fontWeight: 800 },
  // The week strip scrolls sideways rather than wrapping: a term is fifteen
  // weeks, and a wrapped grid of them stops reading as a timeline.
  weekStrip: {
    display: 'flex', gap: 6, overflowX: 'auto', padding: '2px 2px 8px', marginBottom: 6,
    scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch',
  },
  weekChip: {
    flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1,
    background: '#fff', border: '1.5px solid #dadce0', borderRadius: 10, padding: '6px 11px',
    cursor: 'pointer', color: '#202124', font: 'inherit',
  },
  weekChipOn: { background: '#1a73e8', borderColor: '#1a73e8', color: '#fff' },
  weekChipLabel: { fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' },
  weekChipCount: { fontSize: 11.5, color: '#5f6368', whiteSpace: 'nowrap' },
  weekChipCountOn: { color: '#e8f0fe' },
  weekHead: { display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', margin: '4px 0 10px' },
  weekTitle: { fontSize: 17, fontWeight: 800 },
  collapseHead: {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%', boxSizing: 'border-box',
    padding: '12px 14px', border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left',
    font: 'inherit', fontSize: 13, color: 'inherit',
  },
  collapseTitle: { fontSize: 14.5, fontWeight: 800 },
  tally: {
    background: '#1a73e8', color: '#fff', borderRadius: 999, padding: '2px 10px',
    fontWeight: 800, fontSize: 13, fontVariantNumeric: 'tabular-nums',
  },
  referralGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 },
  referralRow: { display: 'flex', alignItems: 'center', gap: 8 },
  kindReport: { background: '#f1f3f4', color: '#3c4043' },
  groupRow: { background: '#f1f3f4' },
  groupCell: { fontWeight: 800, color: '#202124' },
  tdIndent: { paddingLeft: 24 },
};
